import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import authService from '../../api/services/authService';
import LoginForm from '../../components/auth/LoginForm/LoginForm';
import ResetPassword from '../../components/auth/ResetPassword/ResetPassword';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import OTPVerification from '../../components/auth/OTPVerification/OTPVerification';
import ForgotPassword from '../../components/auth/ForgotPassword/ForgotPassword';

const Login = () => {
  const { login, loginWithOTP, verifyOTP: verifyOTPContext } = useAuth();
  const [currentView, setCurrentView] = useState('login');
  const [userCredentials, setUserCredentials] = useState(null);

  // Mutations
  const loginMutation = useMutation({
    mutationFn: ({ email, password, rememberMe }) => login(email, password, rememberMe),
    onSuccess: (result) => {
      if (result.success) {
        toast.success('Login successful!');
      } else {
        toast.error(result.error || 'Login failed');
      }
    },
    onError: (error) => {
      toast.error(error.message || 'An unexpected error occurred');
    }
  });

  const otpLoginMutation = useMutation({
    mutationFn: ({ emailOrPhone, inputType }) => {
      if (inputType === 'phone') {
        let phoneNumber = emailOrPhone;
        if (!phoneNumber.startsWith('+')) phoneNumber = '+91' + phoneNumber;
        return authService.loginMobileOTP(phoneNumber);
      }
      return authService.loginWithEmailOTP(emailOrPhone);
    },
    onSuccess: (result, variables) => {
      if (result.success) {
        toast.success('OTP sent successfully!');
        setUserCredentials({
          emailOrPhone: variables.emailOrPhone,
          inputType: variables.inputType,
          rememberMe: variables.rememberMe,
          otpData: result.data
        });
        setCurrentView('otp');
      } else {
        toast.error(result.error || 'Failed to send OTP');
      }
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to send OTP. Please try again.');
    }
  });

  const verifyOTPLoginMutation = useMutation({
    mutationFn: async ({ otp }) => {
      let phoneNumber = userCredentials.emailOrPhone;
      if (userCredentials.inputType === 'phone' && !phoneNumber.startsWith('+')) {
        phoneNumber = '+91' + phoneNumber;
      }

      const verifyFn = userCredentials.inputType === 'phone'
        ? authService.verifyMobileOTP(phoneNumber, otp, userCredentials.rememberMe)
        : authService.verifyEmailOTP(userCredentials.emailOrPhone, otp, userCredentials.rememberMe);

      return verifyFn;
    },
    onSuccess: async (result) => {
      if (result.success) {
        toast.success('OTP verified successfully!');
        setUserCredentials(null);
        await loginWithOTP(result.user);
      } else {
        toast.error(result.error || 'OTP verification failed');
      }
    },
    onError: (error) => {
      toast.error(error.message || 'OTP verification failed');
    }
  });

  const resetPasswordMutation = useMutation({
    mutationFn: ({ email, otp, newPassword }) => authService.resetPassword(email, otp, newPassword),
    onSuccess: (result) => {
      if (result.success) {
        toast.success('Password reset successfully!');
        setCurrentView('login');
      } else {
        toast.error(result.error || 'Failed to reset password');
      }
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to reset password. Please try again.');
    }
  });

  const handleLogin = (email, password, rememberMe) => {
    loginMutation.mutate({ email, password, rememberMe });
  };

  const handleForgotPassword = async (email) => {
    setCurrentView('forgot-password');
  };

  const handleResetPassword = () => {
    setCurrentView('reset-password');
  };

  const handleResetPasswordSubmit = (email, otp, newPassword) => {
    resetPasswordMutation.mutate({ email, otp, newPassword });
  };

  const handleOTPLogin = (emailOrPhone, inputType, rememberMe) => {
    otpLoginMutation.mutate({ emailOrPhone, inputType, rememberMe });
  };

  const handleVerifyOTP = (otp) => {
    if (!userCredentials || !userCredentials.emailOrPhone) {
      toast.error('Session expired. Please login again.');
      setCurrentView('login');
      return;
    }

    verifyOTPLoginMutation.mutate({ otp });
  };

  const handleBackToLogin = () => {
    setCurrentView('login');
    setUserCredentials(null);
  };

  const renderCurrentView = () => {
    switch (currentView) {
      case 'otp':
        return (
          <OTPVerification
            onBackToLogin={handleBackToLogin}
            onVerifyOTP={handleVerifyOTP}
            loading={verifyOTPLoginMutation.isPending}
            userEmail={userCredentials?.emailOrPhone}
            otpData={userCredentials?.otpData}
            userCredentials={userCredentials}
          />
        );
      case 'forgot-password':
        return (
          <ForgotPassword
            onBackToLogin={handleBackToLogin}
          />
        );
      case 'reset-password':
        return (
          <ResetPassword
            onBackToLogin={handleBackToLogin}
            onResetPassword={handleResetPasswordSubmit}
            loading={resetPasswordMutation.isPending}
          />
        );
      default:
        return (
          <LoginForm
            onLogin={handleLogin}
            onForgotPassword={handleForgotPassword}
            onOTPLogin={handleOTPLogin}
            onResetPassword={handleResetPassword}
            loading={loginMutation.isPending || otpLoginMutation.isPending}
          />
        );
    }
  };

  return (
    <div className="min-h-screen">
      {renderCurrentView()}
    </div>
  );
};

export default Login;
